import { ETelemetryTopOf, RequestTimelinePoint } from 'multirpc-sdk';
import { createSelector, weakMapMemoize } from 'reselect';
import { ChainID } from '@ankr.com/chains-list';
import { IChartData } from '@ankr.com/telemetry';

import { RootState } from 'store';
import { aggregateBlockchainsCountsByChainID } from 'modules/stats/utils/aggregateBlockchainsCountsByChainID';
import { deepEqulityCheck } from 'modules/common/utils/deepEqualityCheck';
import { mapSharkChainToChainPath } from 'modules/chains/utils/mapSharkChainNameToChainPath';
import {
  selectAllChainsPaths,
  selectAllPathsByChainId,
  selectBlockchains,
  selectPublicBlockchains,
} from 'modules/chains/store/selectors';
import { mapStatsForRequestsWidget } from 'domains/statistics/store/utils/mapStatsForRequestsWidget';

import { BlockchainsCountsMap, CountriesCountsMap } from '../../types';
import {
  IFetchPublicStatsParams,
  selectPublicStats,
} from '../../actions/fetchPublicStats';
import { getTopElementsById } from '../../utils/getTopElementsById';
import { sumBlockchainsCountsByChainPaths } from '../../utils/sumBlockchainsCountsByChainPaths';

const timelineFallback: RequestTimelinePoint[] = [];

const selectPublicBlockchainsCounts = createSelector(
  selectPublicStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.BLOCKCHAIN,
    }),
);

const selectPublicCountriesCounts = createSelector(
  selectPublicStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.COUNTRY,
    }),
);

export const selectPublicBlockchainsCountsMap = createSelector(
  selectPublicBlockchainsCounts,
  blockchainCounts =>
    blockchainCounts.reduce<BlockchainsCountsMap>((map, { count, name }) => {
      map[mapSharkChainToChainPath(name)] = Number(count);

      return map;
    }, {}),
);

export const selectPublicCountriesCountsMap = createSelector(
  selectPublicCountriesCounts,
  countriesCounts =>
    countriesCounts.reduce<CountriesCountsMap>(
      (countryMap, { count, name: country }) => ({
        ...countryMap,
        [country]: { country, requests: Number(count) },
      }),
      {},
    ),
);

const selectPublicUsageTimeline = createSelector(
  selectPublicStats,
  ({ request_timelines }) => {
    const usageTimeline = request_timelines.find(
      ({ name }) => name === 'Usage',
    );

    return usageTimeline?.points ?? timelineFallback;
  },
);

const selectPublicRequestsChartData = createSelector(
  selectPublicUsageTimeline,
  usageTimeline =>
    usageTimeline.map<IChartData>(({ time, value }) => ({
      time: new Date(time),
      value,
    })),
);

export const selectMappedPublicRequestsChartData = createSelector(
  selectPublicRequestsChartData,
  (_state: RootState, statsParams: IFetchPublicStatsParams) => statsParams,
  (data, { timeframe, timeframeShift }) => {
    return mapStatsForRequestsWidget({
      data,
      timeframe,
      timeframeShift,
      shouldHideLastPointIfSmall: true,
    });
  },
);

export const selectPublicTotalRequests = createSelector(
  selectPublicStats,
  ({ total }) => Number(total),
);

export const selectPublicBlockchainCountByChainID = createSelector(
  selectPublicBlockchainsCounts,
  (state: RootState, _params: IFetchPublicStatsParams, chainId: ChainID) => ({
    chainId,
    state,
  }),
  (blockchainsCounts, { chainId, state }) => {
    const chainPaths = selectAllPathsByChainId(state, chainId);

    return sumBlockchainsCountsByChainPaths({
      blockchainsCounts,
      chainPaths,
    });
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
    memoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectPublicAggregatedBlockchainsCountsByChainID = createSelector(
  selectBlockchains,
  selectPublicBlockchains,
  selectAllChainsPaths,
  selectPublicBlockchainsCounts,
  // eslint-disable-next-line max-params
  (blockchains, formattedBlockchains, allChainsPaths, blockchainsCounts) =>
    aggregateBlockchainsCountsByChainID({
      allChainsPaths,
      blockchains,
      blockchainsCounts,
      formattedBlockchains,
    }),
);
